import { motion, useMotionValue, usePresence, useTransform } from "framer-motion";
import { useState, useEffect, useMemo } from "react";

import { Attribute } from "../../../gql/graphql";
import { CARD_COMMIT_X, CARD_EXIT_DURATION, ROTATE_RANGE } from "../../../constants";
import { Content } from "./Content";
import { ExitSwiperCard } from "./ExitSwiperCard";
import { Overlay } from "./Overlay";

import styles from "./Card.module.scss";

export interface SwiperCardProps {
  attribute: Attribute;
  onSwipe: (result: boolean) => unknown;
  showIcons?: boolean;
}

export const SwiperCard = ({ attribute, onSwipe, showIcons }: SwiperCardProps) => {
  const x = useMotionValue(0);
  const [isSwipeRight, setIsSwipRight] = useState(false);
  useTransform(x, (value) => {
    if (value > 0 && !isSwipeRight) {
      setIsSwipRight(true);
    } else if (value < 0 && isSwipeRight) {
      setIsSwipRight(false);
    }
  });
  const [swipeResult, setSwipeResult] = useState<boolean | undefined>();
  const [isPresent, safeToRemove] = usePresence();
  const rotate = useTransform(x, [-CARD_COMMIT_X, CARD_COMMIT_X], [-ROTATE_RANGE, ROTATE_RANGE]);
  const exitX: number | undefined = useMemo(() => (isPresent ? undefined : x.get()), [isPresent]);

  useEffect(() => {
    if (swipeResult !== undefined) {
      onSwipe(swipeResult);
    }
  }, [swipeResult]);

  useEffect(() => {
    if (!isPresent) {
      setTimeout(safeToRemove, CARD_EXIT_DURATION * 1000);
    }
  }, [isPresent]);

  if ((swipeResult !== undefined || !isPresent) && exitX !== undefined) {
    return <ExitSwiperCard exitX={exitX} showIcons={showIcons} result={swipeResult} attribute={attribute} />;
  }

  const windowWidth = window.innerWidth;

  return (
    <motion.div
      drag="x"
      dragConstraints={{ left: -windowWidth, right: windowWidth }}
      dragSnapToOrigin={true}
      dragTransition={{
        bounceStiffness: 400,
        bounceDamping: 20,
      }}
      onDragEnd={(_event, info) => {
        if (info.offset.x > CARD_COMMIT_X) {
          setSwipeResult(true);
        } else if (info.offset.x < -CARD_COMMIT_X) {
          setSwipeResult(false);
        }
      }}
      className={styles.base}
      style={{ x, rotate }}
    >
      <Content attribute={attribute}>
        <Overlay currentX={x} showIcons={showIcons} />
      </Content>
    </motion.div>
  );
};
